/**
 * Sistema de Internacionalização (i18n)
 * 
 * Estrutura básica para gerenciar textos e traduções.
 * Facilita a customização de textos sem alterar o código.
 */

export type Locale = 'pt-BR' | 'en-US' | 'es-ES'

export interface Translations {
  [key: string]: string | Translations
}

/**
 * Traduções em Português (Brasil) - Domínio de Baterias
 */
const ptBR: Translations = {
  product: {
    singular: 'Bateria',
    plural: 'Baterias',
    catalog: 'Catálogo de Baterias',
    details: 'Detalhes da Bateria',
    search: 'Buscar baterias',
    notFound: 'Nenhuma bateria encontrada',
    loading: 'Carregando baterias...',
    addToCart: 'Adicionar ao Carrinho',
    buyNow: 'Comprar Agora',
    inStock: 'Em Estoque',
    outOfStock: 'Fora de Estoque',
    stock: 'Estoque',
    price: 'Preço',
    originalPrice: 'Preço Original',
    discount: 'Desconto',
    rating: 'Avaliação',
    reviews: 'Avaliações',
    specifications: 'Especificações',
    description: 'Descrição',
    related: 'Baterias Relacionadas',
    compatible: 'Compatível com',
    capacity: 'Capacidade',
    voltage: 'Voltagem',
    dimensions: 'Dimensões',
    weight: 'Peso'
  },
  filters: {
    title: 'Filtros',
    brand: 'Marca',
    category: 'Categoria',
    price: 'Preço',
    capacity: 'Capacidade (Ah)',
    voltage: 'Voltagem',
    inStock: 'Apenas em estoque',
    clear: 'Limpar Filtros',
    apply: 'Aplicar Filtros',
    min: 'Mínimo',
    max: 'Máximo'
  },
  cart: {
    title: 'Carrinho',
    empty: 'Seu carrinho está vazio',
    addItem: 'Adicionar ao Carrinho',
    removeItem: 'Remover do Carrinho',
    updateQuantity: 'Atualizar Quantidade',
    subtotal: 'Subtotal',
    total: 'Total',
    checkout: 'Finalizar Compra'
  },
  common: {
    loading: 'Carregando...',
    error: 'Erro',
    success: 'Sucesso',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    save: 'Salvar',
    edit: 'Editar',
    delete: 'Excluir',
    search: 'Buscar',
    filter: 'Filtrar',
    sort: 'Ordenar',
    view: 'Visualizar',
    back: 'Voltar',
    next: 'Próximo',
    previous: 'Anterior',
    close: 'Fechar'
  }
}

/**
 * Traduções em Inglês - Domínio Genérico
 */
const enUS: Translations = {
  product: {
    singular: 'Product',
    plural: 'Products',
    catalog: 'Product Catalog',
    details: 'Product Details',
    search: 'Search products',
    notFound: 'No products found',
    loading: 'Loading products...',
    addToCart: 'Add to Cart',
    buyNow: 'Buy Now',
    inStock: 'In Stock',
    outOfStock: 'Out of Stock',
    stock: 'Stock',
    price: 'Price',
    originalPrice: 'Original Price',
    discount: 'Discount',
    rating: 'Rating',
    reviews: 'Reviews',
    specifications: 'Specifications',
    description: 'Description',
    related: 'Related Products',
    compatible: 'Compatible with',
    capacity: 'Capacity',
    voltage: 'Voltage',
    dimensions: 'Dimensions',
    weight: 'Weight'
  },
  filters: {
    title: 'Filters',
    brand: 'Brand',
    category: 'Category',
    price: 'Price',
    capacity: 'Capacity (Ah)',
    voltage: 'Voltage',
    inStock: 'In Stock Only',
    clear: 'Clear Filters',
    apply: 'Apply Filters',
    min: 'Min',
    max: 'Max'
  },
  cart: {
    title: 'Cart',
    empty: 'Your cart is empty',
    addItem: 'Add to Cart',
    removeItem: 'Remove from Cart',
    updateQuantity: 'Update Quantity',
    subtotal: 'Subtotal',
    total: 'Total',
    checkout: 'Checkout'
  },
  common: {
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    edit: 'Edit',
    delete: 'Delete',
    search: 'Search',
    filter: 'Filter',
    sort: 'Sort',
    view: 'View',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    close: 'Close'
  }
}

/**
 * Traduções em Espanhol - Domínio Genérico
 */
const esES: Translations = {
  product: {
    singular: 'Producto',
    plural: 'Productos',
    catalog: 'Catálogo de Productos',
    details: 'Detalles del Producto',
    search: 'Buscar productos',
    notFound: 'No se encontraron productos',
    loading: 'Cargando productos...',
    addToCart: 'Agregar al Carrito',
    buyNow: 'Comprar Ahora',
    inStock: 'En Stock',
    outOfStock: 'Fuera de Stock',
    stock: 'Stock',
    price: 'Precio',
    originalPrice: 'Precio Original',
    discount: 'Descuento',
    rating: 'Calificación',
    reviews: 'Reseñas',
    specifications: 'Especificaciones',
    description: 'Descripción',
    related: 'Productos Relacionados',
    compatible: 'Compatible con',
    capacity: 'Capacidad',
    voltage: 'Voltaje',
    dimensions: 'Dimensiones',
    weight: 'Peso'
  },
  filters: {
    title: 'Filtros',
    brand: 'Marca',
    category: 'Categoría',
    price: 'Precio',
    capacity: 'Capacidad (Ah)',
    voltage: 'Voltaje',
    inStock: 'Solo en Stock',
    clear: 'Limpiar Filtros',
    apply: 'Aplicar Filtros',
    min: 'Mín',
    max: 'Máx'
  },
  cart: {
    title: 'Carrito',
    empty: 'Tu carrito está vacío',
    addItem: 'Agregar al Carrito',
    removeItem: 'Eliminar del Carrito',
    updateQuantity: 'Actualizar Cantidad',
    subtotal: 'Subtotal',
    total: 'Total',
    checkout: 'Finalizar Compra'
  },
  common: {
    loading: 'Cargando...',
    error: 'Error',
    success: 'Éxito',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    save: 'Guardar',
    edit: 'Editar',
    delete: 'Eliminar',
    search: 'Buscar',
    filter: 'Filtrar',
    sort: 'Ordenar',
    view: 'Ver',
    back: 'Volver',
    next: 'Siguiente',
    previous: 'Anterior',
    close: 'Cerrar'
  }
}

/**
 * Mapa de traduções por locale
 */
const translations: Record<Locale, Translations> = {
  'pt-BR': ptBR,
  'en-US': enUS,
  'es-ES': esES
}

/**
 * Locale ativo (pode ser configurado via env ou contexto)
 */
let currentLocale: Locale = 'pt-BR'

/**
 * Define o locale ativo
 */
export const setLocale = (locale: Locale): void => {
  currentLocale = locale
}

/**
 * Obtém o locale ativo
 */
export const getLocale = (): Locale => {
  return currentLocale
}

/**
 * Obtém uma tradução por chave
 * 
 * @example
 * t('product.singular') // 'Bateria'
 * t('product.catalog') // 'Catálogo de Baterias'
 * t('common.loading') // 'Carregando...'
 */
export const t = (key: string, locale?: Locale): string => {
  const activeLocale = locale || currentLocale
  const translation = translations[activeLocale]
  
  if (!translation) {
    console.warn(`Translation not found for locale: ${activeLocale}`)
    return key
  }
  
  const keys = key.split('.')
  let value: any = translation
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k]
    } else {
      console.warn(`Translation key not found: ${key} in locale: ${activeLocale}`)
      return key
    }
  }
  
  if (typeof value === 'string') {
    return value
  }
  
  return key
}

/**
 * Hook para usar traduções em componentes React
 * 
 * @example
 * const { t, locale, setLocale } = useTranslation()
 * <h1>{t('product.catalog')}</h1>
 */
export const useTranslation = () => {
  return {
    t: (key: string) => t(key),
    locale: getLocale(),
    setLocale: (locale: Locale) => setLocale(locale)
  }
}

/**
 * Obtém todas as traduções de um namespace
 * 
 * @example
 * const productTranslations = getTranslations('product')
 */
export const getTranslations = (namespace: string, locale?: Locale): Translations | undefined => {
  const activeLocale = locale || currentLocale
  const translation = translations[activeLocale]
  
  if (!translation || typeof translation !== 'object') {
    return undefined
  }
  
  return translation[namespace] as Translations | undefined
}

export default {
  t,
  setLocale,
  getLocale,
  useTranslation,
  getTranslations
}

